import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import NumberFormat from 'react-number-format';

import { getCurrencySymbol } from '../../../collums-components/helpers';

import { withStyles, Typography, Box, Button } from '@material-ui/core';
import { drawerStyles } from './styles';
import { APPOINTMENT_STATUS_TYPES } from '../../../collums-constants';
import CancelContinueModal from '../../common/CancelContinueModal';

function AppointmentMembership({
    classes,
    data,
    serviceData,
    membershipName,
    handleChange,
    form,
    clinic,
    isMembershipServicePerformedByPractitioner,
    isFirstService
}) {
    const [showEarlyBookingModal, setShowEarlyBookingModal] = useState(false);
    const [showOverdueModal, setShowOverdueModal] = useState(false);

    const usesThisService = appt =>
        appt?.customerMembership === data.id && appt?.membershipData?.serviceIndex === serviceData.serviceIndex;
    const isBooked = usesThisService(form);
    // Main appointment + linked appointments of the same booking
    const rootForm = form.parentForm || form;
    const groupAppointments = [rootForm, ...(rootForm.linkedAppointments || [])];
    const bookedElsewhereCount = groupAppointments.filter(usesThisService).length - (isBooked ? 1 : 0);
    const isBookedElsewhere = !isBooked && bookedElsewhereCount >= (serviceData.quantity || 1);
    const allUsed = (serviceData.available || 0) <= 0;
    const isOneOffCompleted = serviceData.serviceType === 'one-off' && (serviceData.isCompleted || allUsed);
    const bookNextApptFrom = serviceData.bookNextApptFrom;

    const formatDate = dateStr => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = String(d.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    };

    const bookAppointment = () => {
        try {
            if (form.checkedIn) return;
            if (form.status === APPOINTMENT_STATUS_TYPES.COMPLETED) return;

            const service = serviceData.service;
            let duration = service.defaultDuration;
            const practitioner = form.practitioners?.[0];
            if (practitioner) {
                const location = service.locations?.find(element => clinic.id === element.clinic);
                if (location) {
                    const staff = location.staffs?.find(el => el.staff === practitioner.id);
                    if (staff?.duration) duration = staff.duration;
                }
            }
            const end = form.event.start.clone().add(duration, 'minutes');
            handleChange({
                ...form,
                customerMembership: data.id,
                membershipData: {
                    serviceIndex: serviceData.serviceIndex,
                    periodIndex: serviceData.periodIndex,
                    position: 1,
                    redemptionValue: 0
                },
                service: serviceData.service,
                price: 0,
                event: {
                    start: form.event.start,
                    end
                },
                duration
            });
        } catch (e) {
            console.error(e);
            toastr.error('Something went wrong (code: m0001)');
        }
    };

    const handleBookClick = () => {
        if (form.checkedIn || form.status === APPOINTMENT_STATUS_TYPES.COMPLETED) return;

        if (isBooked) {
            // Unbook - reset service to the empty state (as after "Add another service"), user picks it again
            handleChange({
                ...form,
                customerMembership: undefined,
                membershipData: undefined,
                service: 'Search for service',
                price: undefined
            });
            return;
        }

        if (isOverdue) {
            setShowOverdueModal(true);
            return;
        }

        if (isOneOffCompleted || isBookedElsewhere) return;

        if (allUsed) {
            setShowEarlyBookingModal(true);
            return;
        }

        bookAppointment();
    };

    const nextPaymentDue = data.nextPaymentDue;
    const isOverdue = data.isOverdue;
    const isPaused = data.status === 'paused';
    const customerId = data.customer?.id || data.customer;
    const membershipLink = `/customer/${customerId}/memberships`;

    return (
        <>
            {isFirstService && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant="h4">
                            <a
                                href={membershipLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1a73e8', textDecoration: 'none', fontWeight: 'bold' }}
                            >
                                Membership: {membershipName}
                            </a>
                        </Typography>
                    </div>
                    <Typography style={{ fontSize: '0.9rem' }}>
                        <NumberFormat
                            value={data.pricePerMonth || 0}
                            fixedDecimalScale
                            decimalScale={2}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={getCurrencySymbol()}
                        />
                        {' per month'}
                    </Typography>
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <Box display="flex" justifyContent="space-between" flexGrow={1}>
                            <Typography style={{ fontSize: '0.9rem' }}>Next payment due:</Typography>
                            <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                {formatDate(nextPaymentDue)}
                            </Typography>
                        </Box>
                        <Box ml={2} style={{ minWidth: 80, textAlign: 'center' }}>
                            {isOverdue && (
                                <Typography
                                    style={{
                                        color: '#bf2000',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    OVERDUE
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </>
            )}

            <Box display="flex" flexDirection="row" mt={isFirstService ? 2 : 0} alignItems="center">
                <Box display="flex" flexDirection="column" flexGrow={1} className={classes.courseValues}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography style={{ fontSize: '0.9rem' }}>Service:</Typography>
                        <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                            {serviceData.service?.name}
                        </Typography>
                    </Box>
                    {!isMembershipServicePerformedByPractitioner && (
                        <Typography variant="h5">Practitioner does not perform this service</Typography>
                    )}
                    <Box display="flex" mt={'5px'} justifyContent="space-between">
                        <Typography>Freq:</Typography>
                        <Typography style={{ fontWeight: 500 }}>
                            {serviceData.serviceType === 'one-off'
                                ? `One-off x${serviceData.quantity || 1}`
                                : `${serviceData.quantity || 1} every ${serviceData.repeatEvery || 1} month(s)`}
                        </Typography>
                    </Box>
                    <Box display="flex" mt={'5px'} justifyContent="space-between">
                        <Typography>Value each:</Typography>
                        <NumberFormat
                            className={classes.amountPaid}
                            value={(serviceData.valueEach || 0).toFixed(2)}
                            fixedDecimalScale
                            decimalScale={2}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={getCurrencySymbol()}
                        />
                    </Box>
                    {bookNextApptFrom && (
                        <Box display="flex" mt={'5px'} justifyContent="space-between">
                            <Typography>Book next appt from</Typography>
                            <Typography style={{ fontWeight: 500 }}>{formatDate(bookNextApptFrom)}</Typography>
                        </Box>
                    )}
                </Box>
                <Box ml={2} display="flex" alignItems="center">
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleBookClick}
                        disabled={
                            !isMembershipServicePerformedByPractitioner ||
                            form.status === APPOINTMENT_STATUS_TYPES.COMPLETED ||
                            isOneOffCompleted ||
                            isBookedElsewhere
                        }
                        style={{
                            backgroundColor:
                                !isMembershipServicePerformedByPractitioner || isOneOffCompleted
                                    ? '#69767E'
                                    : isBooked || isBookedElsewhere
                                    ? '#ffa012'
                                    : isOverdue || isPaused
                                    ? '#d32f2f'
                                    : '#00b669',
                            color: '#fff',
                            borderRadius: 20,
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 80
                        }}
                    >
                        {isBooked || isBookedElsewhere ? 'Booked' : 'Book'}
                    </Button>
                </Box>
            </Box>

            {showEarlyBookingModal && (
                <CancelContinueModal
                    title={'Early appointment booking'}
                    contentText={`This service will not be available to be booked from this membership until ${formatDate(
                        bookNextApptFrom
                    )}. Would you like to continue to book this service early?`}
                    setOpen={setShowEarlyBookingModal}
                    onContinue={() => bookAppointment()}
                />
            )}

            {showOverdueModal && (
                <CancelContinueModal
                    title={'Membership payment overdue'}
                    contentText={
                        'An expected payment(s) for this membership has not been received. Would you like to continue to book this service?'
                    }
                    setOpen={setShowOverdueModal}
                    onContinue={() => {
                        if (isOneOffCompleted) return;
                        if (allUsed) {
                            setShowEarlyBookingModal(true);
                        } else {
                            bookAppointment();
                        }
                    }}
                />
            )}
        </>
    );
}

AppointmentMembership.propTypes = {
    classes: PropTypes.object.isRequired,
    clinic: PropTypes.object.isRequired,
    data: PropTypes.object,
    serviceData: PropTypes.object,
    membershipName: PropTypes.string,
    handleChange: PropTypes.func,
    form: PropTypes.object,
    isMembershipServicePerformedByPractitioner: PropTypes.bool,
    isFirstService: PropTypes.bool
};

export default withStyles(drawerStyles)(AppointmentMembership);
